import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { CSVLink } from 'react-csv';
import { Icon, Dropdown } from 'semantic-ui-react';
import { isIos, isAppleMac } from '../../../businessLogic/browser';
import { sortUsersIcanManage } from '../../../businessLogic/sorting';
import { displayGlobalMessage } from '../../../actions/globalMessage';
import copy from 'copy-to-clipboard';

const EmailUsersButton = ({
  userEmails,
  dispatch,
  isUsersAvailable,
  entityName,
  areUsersLoaded,
  isDesktop,
  isTab,
  isMobile,
  handleOpen,
  handleClose,
}) => {
  const areMoreThan50Users = userEmails && userEmails.length > 50;

  // Getting all users emails
  const getAllUsersEmails = () => {
    if (userEmails && userEmails.length > 0) {
      const emailSeparator = isIos() || isAppleMac() ? ',' : ';';
      return sortUsersIcanManage(userEmails).join(emailSeparator);
    }
  };

  // Sorting and listing user's data for CSV creation
  const getAllUsersForCSV = () => {
    if (userEmails && userEmails.length > 0) {
      return sortUsersIcanManage(userEmails)
        .sort()
        .map((i) => {
          return {
            email: i,
          };
        });
    }
  };

  // Click handler for email all
  const onEmailAllButtonClick = () => {
    if (!isUsersAvailable) return;

    const mailToLink = 'mailto:?bcc=' + getAllUsersEmails();
    window.location.href = mailToLink;

    const successMessage = APP.LANGUAGE.EmailUsers.EmailAllUsersSuccessMessage;
    dispatch(
      displayGlobalMessage({
        text: successMessage,
        visible: true,
        messageType: APP.LANGUAGE.GlobalMessageTypes.Good,
        autoHide: true,
      })
    );
  };

  // Click handler for copy all email
  const onCopyAllEmailButtonClick = () => {
    if (!isUsersAvailable) return;
    copy(getAllUsersEmails());
    dispatch(
      displayGlobalMessage({
        text: APP.LANGUAGE.EmailUsers.CopyEmailUsersSuccessMessage,
        visible: true,
        messageType: APP.LANGUAGE.GlobalMessageTypes.Good,
        autoHide: true,
      })
    );
  };

  // Getting current date
  const currentDate = moment().format('YYYYMMDD');

  // Email users CSV Header
  const userEmailsCSVHeader = [{ label: 'Email', key: 'email' }];

  // Email users CSV report with data, header and filename
  const userEmailsCSVReport = {
    data: getAllUsersForCSV() && getAllUsersForCSV().lenght !== 0 ? getAllUsersForCSV() : [{}],
    headers: userEmailsCSVHeader,
    filename: entityName + '_Users_' + currentDate + '.csv',
  };

  // Email users dropdown button markup for desktop
  const emailUsersMarkupForDesktop = (
    <button type="button" className="ui labeled icon button right-hand-side-button color-mid-grey-bg color-white">
      <i className="envelope icon" />
      {APP.LANGUAGE.EmailUsers.Title}
    </button>
  );

  // Email users dropdown button markup for tablet
  const emailUsersMarkupForTablet = (
    <button type="button" className="ui labeled icon button right-hand-side-button color-mid-grey-bg color-white">
      <i
        className="envelope icon"
        ref={(el) => {
          if (el) {
            el.style.setProperty('margin-top', '-1px', 'important');
            el.style.setProperty('font-size', '1em', 'important');
          }
        }}
      />
      {APP.LANGUAGE.EmailUsers.Title}
    </button>
  );

  // Email users dropdown button markup for mobile
  const emailUsersMarkupForMobile = (
    <div className="email-user-mob-icon-wrapper">
      <i
        className="envelope icon"
        ref={(el) => {
          if (el) {
            el.style.setProperty('font-size', '1.3em', 'important');
          }
        }}
      />
    </div>
  );

  // Email users dropdown menu items markup
  const emailUsersDropDownMenuMarkup = (
    <Dropdown.Menu className="max-width">
      <Dropdown.Item
        className="inline-flex max-width"
        onClick={onEmailAllButtonClick}
        disabled={!areMoreThan50Users ? false : true}
      >
        <Icon name="envelope" />
        <span className="text">{APP.LANGUAGE.EmailUsers.EmailAllUsers}</span>
      </Dropdown.Item>
      <Dropdown.Item onClick={onCopyAllEmailButtonClick}>
        <Icon name="copy" />
        <span className="text">{APP.LANGUAGE.EmailUsers.CopyEmailAllUsers}</span>
      </Dropdown.Item>
      <Dropdown.Item>
        <Icon name="download" />
        <CSVLink className="email-users-csv-link text" {...userEmailsCSVReport}>
          {APP.LANGUAGE.EmailUsers.DownloadUsersCSV}
        </CSVLink>
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  // Email users dropDown menu items markup for mobile
  const emailUsersDropDownMenuMarkupForMobile = (
    <Dropdown.Menu className="max-width">
      <Dropdown.Item
        className="inline-flex max-width"
        onClick={onEmailAllButtonClick}
        disabled={!areMoreThan50Users ? false : true}
      >
        <Icon name="envelope" />
        <span className="text">{APP.LANGUAGE.EmailUsers.EmailAllUsersMob}</span>
      </Dropdown.Item>
      <Dropdown.Item
        className="copy-to-clipboard-email-all-users"
        data-clipboard-text={getAllUsersEmails()}
        onClick={onCopyAllEmailButtonClick}
      >
        <Icon name="copy" />
        <span className="text">{APP.LANGUAGE.EmailUsers.CopyEmailAllUsersMob}</span>
      </Dropdown.Item>
      <Dropdown.Item>
        <Icon name="download" />
        <CSVLink className="email-users-csv-link text" {...userEmailsCSVReport}>
          {APP.LANGUAGE.EmailUsers.DownloadUsersCSV}
        </CSVLink>
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  return (
    <Dropdown
      trigger={
        (isDesktop && emailUsersMarkupForDesktop) ||
        (isTab && emailUsersMarkupForTablet) ||
        (isMobile && emailUsersMarkupForMobile)
      }
      icon="caret down"
      id="email-users-dropdown"
      className="inline-flex center-flex space-between full-height bold color-bad-things-text"
      disabled={!areUsersLoaded || !isUsersAvailable ? true : false}
      onOpen={handleOpen}
      onClose={handleClose}
    >
      {(isDesktop && emailUsersDropDownMenuMarkup) ||
        (isTab && emailUsersDropDownMenuMarkup) ||
        (isMobile && emailUsersDropDownMenuMarkupForMobile)}
    </Dropdown>
  );
};

export default EmailUsersButton;

EmailUsersButton.propTypes = {
  userEmails: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
  isUsersAvailable: PropTypes.bool.isRequired,
  entityName: PropTypes.string.isRequired,
  areUsersLoaded: PropTypes.bool.isRequired,
  handleOpen: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool,
  isTab: PropTypes.bool,
  isMobile: PropTypes.bool,
};

EmailUsersButton.defaultProps = {
  isDesktop: false,
  isTab: false,
  isMobile: false,
};
